/**
 * useTranscription — Calls backend STT endpoint to transcribe audio.
 *
 * Uploads an audio Blob to POST /api/control/v1/media/transcribe
 * and returns the transcribed text.
 *
 * The backend uses DeepgramProvider (Nova-2) for transcription.
 * See: packages/providers/src/providers/media/deepgram.py
 */

import { useCallback, useState } from 'react';
import { apiClient } from '../lib/api-client';

interface TranscriptionResult {
  text: string;
  confidence?: number;
  duration_seconds?: number;
}

interface UseTranscriptionReturn {
  /** Send audio blob to backend for transcription */
  transcribe: (blob: Blob) => Promise<TranscriptionResult | null>;
  /** Whether a transcription is in progress */
  isTranscribing: boolean;
  /** Error message if transcription failed */
  error: string | null;
}

export function useTranscription(): UseTranscriptionReturn {
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const transcribe = useCallback(async (blob: Blob, language?: string): Promise<TranscriptionResult | null> => {
    setError(null);
    setIsTranscribing(true);

    try {
      const formData = new FormData();
      formData.append('audio', blob, 'recording.webm');
      if (language) {
        formData.append('language', language);
      }

      // Use raw fetch since apiClient.post expects JSON, not multipart
      const tenantId = apiClient.getTenantId();
      const response = await fetch('/api/control/v1/media/transcribe', {
        method: 'POST',
        body: formData,
        credentials: 'include',
        headers: {
          // Let browser set Content-Type with boundary for multipart
          ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
        },
      });

      if (!response.ok) {
        const detail = await response.text();
        throw new Error(`Transcription failed: ${response.status} ${detail}`);
      }

      const data = await response.json();
      return {
        text: data.text,
        confidence: data.confidence,
        duration_seconds: data.duration_seconds,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Transcription failed';
      setError(message);
      return null;
    } finally {
      setIsTranscribing(false);
    }
  }, []);

  return { transcribe, isTranscribing, error };
}
